import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TimesheetReStructuringServiceProxy, TimesheetCompletionStatusDto, UserServiceProxy } from '@shared/service-proxies/service-proxies';

export interface ReminderProject {
  name: string;
  days?: string[];
  reminderEnabled: boolean;
}

export interface TimesheetReminderCache {
  /** True once the API call has completed (success or error). */
  loaded: boolean;
  /** Non-empty when there are pending timesheets. */
  projects: ReminderProject[];
}

const EMPTY_CACHE: TimesheetReminderCache = { loaded: false, projects: [] };

@Injectable({ providedIn: 'root' })
export class TimesheetReminderStateService {

  private _cache$ = new BehaviorSubject<TimesheetReminderCache>(EMPTY_CACHE);
  private _fetching = false;
  private _downloadPopupActive = false;

  constructor(private _userService: UserServiceProxy) {
    // Bootstrap the flag from localStorage in case the DownloadITPModalShow event
    // fired BEFORE this service was instantiated (lazy root services are created
    // on first injection, which may be after the dashboard fires the event).
    this._downloadPopupActive = localStorage.getItem('isDownloadModalVisible') === 'true';

    abp.event.on('DownloadITPModalShow', () => { this._downloadPopupActive = true; });
    abp.event.on('DownloadITPModalHide', () => {
      this._downloadPopupActive = false;
      localStorage.removeItem('isDownloadModalVisible');
    });

  }

  /** True while the Download Now popup is visible. */
  get isDownloadPopupActive(): boolean {
    return this._downloadPopupActive;
  }

  /**
   * Emits the cached result. Late subscribers receive the latest value immediately
   * via BehaviorSubject, so cached data is delivered synchronously.
   */
  get cache$(): Observable<TimesheetReminderCache> {
    return this._cache$.asObservable();
  }

  /**
   * Triggers the API call only if data has not been fetched yet.
   * Safe to call from every component instance — subsequent calls are no-ops.
   * Users with Auto Submit Timesheet enabled are not prompted to submit manually.
   */
  initialize(userId: number, timesheetService: TimesheetReStructuringServiceProxy): void {
    if (this._cache$.value.loaded || this._fetching) {
      return;
    }

    this._fetching = true;
    this._userService.getAutomatedTimesheetSetting(userId).subscribe({
      next: (isAutomated) => {
        if (isAutomated) {
          this._fetching = false;
          this._cache$.next({ loaded: true, projects: [] });
          return;
        }
        this._loadPendingProjects(userId, timesheetService);
      },
      error: () => {
        this._loadPendingProjects(userId, timesheetService);
      }
    });
  }

  private _loadPendingProjects(userId: number, timesheetService: TimesheetReStructuringServiceProxy): void {
    const offset = new Date().getTimezoneOffset();

    timesheetService.checkLast30DaysTimesheetCompletion(userId, offset).subscribe({
      next: (result: TimesheetCompletionStatusDto) => {
        this._fetching = false;

        if (!result.allTimesheetsCompleted && result.unfilledProjects && result.unfilledProjects.length > 0) {
          const projects: ReminderProject[] = result.unfilledProjects.map((proj: any) => ({
            name: proj.projectName,
            days: proj.pendingDates || [],
            reminderEnabled: proj.isTimesheetReminderEnable === true
          }));
          this._cache$.next({ loaded: true, projects });
        } else {
          this._cache$.next({ loaded: true, projects: [] });
        }
      },
      error: () => {
        this._fetching = false;
        this._cache$.next({ loaded: true, projects: [] });
      }
    });
  }

  /**
   * Clears the cache so the next initialize() triggers a fresh API call.
   * Call this when the user navigates to the timesheet page to submit,
   * so the popup re-checks upon return.
   */
  reset(): void {
    this._cache$.next(EMPTY_CACHE);
    this._fetching = false;
  }

  // ── Per-page session skip state ────────────────────────────────────────────
  // Stored in localStorage (NOT sessionStorage) so that sessionStorage.clear()
  // calls in other modules (Time Tracking, Leaves, Timesheet, Activities) do not
  // wipe the skip flags mid-session.
  //
  // The stored object is { sessionId, pages[] }.  It is automatically stale for
  // any session whose ID does not match the current tsreminder_session_id value,
  // so a new login always starts with a clean slate.

  private static readonly LS_SKIP_KEY = 'tsreminder_skip_state';
  private static readonly SESSION_KEY = 'tsreminder_session_id';

  /**
   * Returns the current session ID from localStorage.
   * If login.component.ts has not stamped one yet (e.g. token still valid from
   * a previous browser session), creates a stable fallback ID on first call so
   * subsequent skip checks within the same browser session are consistent.
   */
  private _getSessionId(): string {
    let id = localStorage.getItem(TimesheetReminderStateService.SESSION_KEY) || '';
    if (!id) {
      id = Date.now().toString();
      localStorage.setItem(TimesheetReminderStateService.SESSION_KEY, id);
    }
    return id;
  }

  private _readSkipState(): { sessionId: string; pages: string[] } {
    try {
      const raw = localStorage.getItem(TimesheetReminderStateService.LS_SKIP_KEY);
      if (raw) { return JSON.parse(raw) as { sessionId: string; pages: string[] }; }
    } catch { /* invalid JSON — treat as empty */ }
    return { sessionId: '', pages: [] };
  }

  /** Record that the user clicked "Skip for now" on the given page this session. */
  skipPage(pageKey: string): void {
    if (!pageKey) { return; }
    const sessionId = this._getSessionId();
    const stored = this._readSkipState();
    // If the stored state belongs to a different session, start fresh.
    const current = stored.sessionId === sessionId
      ? stored
      : { sessionId, pages: [] as string[] };
    if (!current.pages.includes(pageKey)) {
      current.pages.push(pageKey);
    }
    localStorage.setItem(TimesheetReminderStateService.LS_SKIP_KEY, JSON.stringify(current));
  }

  /** Returns true if the user already skipped the popup on this page in the current session. */
  isPageSkipped(pageKey: string): boolean {
    if (!pageKey) { return false; }
    const sessionId = this._getSessionId();
    const stored = this._readSkipState();
    return stored.sessionId === sessionId && stored.pages.includes(pageKey);
  }

  /** Clear all skip flags (e.g. called after timesheet submission or on logout). */
  clearSkipState(): void {
    localStorage.removeItem(TimesheetReminderStateService.LS_SKIP_KEY);
  }
}
